import type { DocumentMeta } from './api';

const STORAGE_PREFIX = 'notepad_';
const DRAFT_PREFIX = `${STORAGE_PREFIX}draft_`;
const ACTIVE_WORKSPACE_KEY = `${STORAGE_PREFIX}active_workspace`;

// Get current workspace ID
function getWorkspaceId(): string {
  return localStorage.getItem(ACTIVE_WORKSPACE_KEY) || 'default';
}

// Tab storage keys are workspace-specific
function getTabsKey(workspaceId?: string): string {
  const wsId = workspaceId || getWorkspaceId();
  return `${STORAGE_PREFIX}tabs_${wsId}`;
}

function getActiveTabKey(workspaceId?: string): string {
  const wsId = workspaceId || getWorkspaceId();
  return `${STORAGE_PREFIX}active_tab_${wsId}`;
}

export interface OpenTab {
  id: string;
  title: string;
  hasUnsavedChanges: boolean;
}

class LocalStorageService {
  // Open tabs management (workspace-aware)
  getOpenTabs(workspaceId?: string): OpenTab[] {
    const data = localStorage.getItem(getTabsKey(workspaceId));
    return data ? JSON.parse(data) : [];
  }

  setOpenTabs(tabs: OpenTab[], workspaceId?: string): void {
    localStorage.setItem(getTabsKey(workspaceId), JSON.stringify(tabs));
  }

  addOpenTab(tab: OpenTab, workspaceId?: string): void {
    const tabs = this.getOpenTabs(workspaceId);
    if (!tabs.find(t => t.id === tab.id)) {
      tabs.push(tab);
      this.setOpenTabs(tabs, workspaceId);
    }
  }

  removeOpenTab(id: string, workspaceId?: string): void {
    const tabs = this.getOpenTabs(workspaceId).filter(t => t.id !== id);
    this.setOpenTabs(tabs, workspaceId);
    this.removeDraft(id);
  }

  updateTabUnsaved(id: string, hasUnsavedChanges: boolean, workspaceId?: string): void {
    const tabs = this.getOpenTabs(workspaceId);
    const tab = tabs.find(t => t.id === id);
    if (tab) {
      tab.hasUnsavedChanges = hasUnsavedChanges;
      this.setOpenTabs(tabs, workspaceId);
    }
  }

  // Active tab (workspace-aware)
  getActiveTabId(workspaceId?: string): string | null {
    return localStorage.getItem(getActiveTabKey(workspaceId));
  }

  setActiveTabId(id: string, workspaceId?: string): void {
    localStorage.setItem(getActiveTabKey(workspaceId), id);
  }

  clearActiveTabId(workspaceId?: string): void {
    localStorage.removeItem(getActiveTabKey(workspaceId));
  }

  // Active workspace
  getActiveWorkspaceId(): string {
    return getWorkspaceId();
  }

  setActiveWorkspaceId(id: string): void {
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
  }

  // Drafts (unsaved content)
  getDraft(id: string): string | null {
    return localStorage.getItem(`${DRAFT_PREFIX}${id}`);
  }

  setDraft(id: string, content: string): void {
    localStorage.setItem(`${DRAFT_PREFIX}${id}`, content);
  }

  removeDraft(id: string): void {
    localStorage.removeItem(`${DRAFT_PREFIX}${id}`);
  }

  // Document cache for offline
  getCachedDocument(id: string): { meta: DocumentMeta; content: string } | null {
    const data = localStorage.getItem(`${STORAGE_PREFIX}doc_${id}`);
    return data ? JSON.parse(data) : null;
  }

  setCachedDocument(id: string, doc: { meta: DocumentMeta; content: string }): void {
    localStorage.setItem(`${STORAGE_PREFIX}doc_${id}`, JSON.stringify(doc));
  }

  removeCachedDocument(id: string): void {
    localStorage.removeItem(`${STORAGE_PREFIX}doc_${id}`);
  }

  // Version tracking for sync
  getLocalVersion(id: string): number {
    return parseInt(localStorage.getItem(`${STORAGE_PREFIX}version_${id}`) || '0');
  }

  setLocalVersion(id: string, version: number): void {
    localStorage.setItem(`${STORAGE_PREFIX}version_${id}`, version.toString());
  }
}

export const storage = new LocalStorageService();
